import type { AgentObject, AuditEvent } from "./api";

/**
 * DEV/QA harness — a single reversible flag (VITE_SUI_PREVIEW=1) that bypasses
 * the wallet gate and serves dummy data so every /sui page + state renders for
 * screenshots without a wallet or backend. Flag OFF (production default) → the
 * real connect gate + live data, untouched. Inert in the prod build.
 */
export const PREVIEW = import.meta.env.VITE_SUI_PREVIEW === "1";

const now = Date.now();
const iso = (msAgo: number) => new Date(now - msAgo).toISOString();
const MIN = 60_000;
const HR = 3_600_000;

const USDC_TYPE = "0xa1ec7fc00a6f40db9693ad1415d0c193ad3906494428cf252621037bd7117e29::usdc::USDC";
const DEEP_TYPE = "0x36dbef866a1d62bf7328989a10fb2f07d769f4ee587c0de4a0a256e57e0a58a8::deep::DEEP";
const SUI_TYPE = "0x2::sui::SUI";

export const PREVIEW_AGENTS = [
  {
    id: "0xa1b2c3d4", name: "deepbook-mm-1", status: "active", chain: "sui", framework: "custom",
    created_at: iso(9 * HR),
    sui_vault_id: "0xc6c30539aa11bb22cc33dd44ee55ff66aa77bb88cc99dd00ee11ff2233445566",
    sui_policy_id: "0x17512ee7bb22cc33dd44ee55ff66aa77bb88cc99dd00ee11ff223344556677aa",
    sui_agent_cap_id: "0xe9ff592ecc33dd44ee55ff66aa77bb88cc99dd00ee11ff22334455667788bbcc",
    policy: {
      per_asset_caps: [
        { asset: "SUI", per_day: 2, per_tx: 0.5, spent_today: 0.9 },
        { asset: "USDC", per_day: 100, per_tx: 25, spent_today: 40 },
      ],
      allowed_actions: ["deepbook_swap", "deepbook_limit_order"],
      expires_at: iso(-18 * HR),
    },
  },
  {
    id: "0xb2c3d4e5", name: "orca-yield-router", status: "paused", chain: "sui", framework: "sak",
    created_at: iso(30 * HR),
    sui_vault_id: "0xd7d416400022113344556677889900aabbccddeeff00112233445566778899aa",
    sui_policy_id: "0x28623ff8113344556677889900aabbccddeeff0011223344556677889900bbcc",
    sui_agent_cap_id: "0xfa006a03224455667788990011223344556677889900aabbccddeeff0011ccdd",
    policy: {
      per_asset_caps: [{ asset: "SUI", per_day: 5, per_tx: 1, spent_today: 0 }],
      allowed_actions: ["deepbook_swap"],
      expires_at: iso(-6 * HR),
    },
  },
  {
    id: "0xc3d4e5f6", name: "arb-sweeper-3", status: "revoked", chain: "sui", framework: "custom",
    created_at: iso(80 * HR), revoked_at: iso(2 * HR), revoked_reason: "manual kill — drift detected",
    sui_vault_id: "0xe8e527511133224455667788990011aabbccddeeff00112233445566778800bb",
    sui_policy_id: "0x39734009224455", sui_agent_cap_id: "0x0b117b14335566",
    policy: {
      per_asset_caps: [{ asset: "USDC", per_day: 2000, per_tx: 500, spent_today: 1850 }],
      allowed_actions: ["deepbook_swap", "deepbook_cancel"],
      expires_at: "",
    },
  },
] as unknown as AgentObject[];

// Live vault holdings per agent (item 6) — what the agent actually holds now.
const BALANCES: Record<string, { coinType: string; amount: string }[]> = {
  "0xc6c30539aa11bb22cc33dd44ee55ff66aa77bb88cc99dd00ee11ff2233445566": [
    { coinType: SUI_TYPE, amount: "920000000" }, { coinType: USDC_TYPE, amount: "60000000" }, { coinType: DEEP_TYPE, amount: "0" },
  ],
  "0xd7d416400022113344556677889900aabbccddeeff00112233445566778899aa": [
    { coinType: SUI_TYPE, amount: "4100000000" },
  ],
  "0xe8e527511133224455667788990011aabbccddeeff00112233445566778800bb": [
    { coinType: USDC_TYPE, amount: "150000000" },
  ],
};
export function previewBalances(vaultId: string): { coinType: string; amount: string }[] {
  return BALANCES[vaultId] ?? [];
}

const B58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
const TX = (n: number) => Array.from({ length: 44 }, (_, i) => B58[(n * 31 + i * 17 + i * i) % 58]).join("");
function ev(i: number, agent_id: string, p: { type: string; decision: "allowed" | "denied" | "n/a"; event_type?: string; amount?: number; asset?: string; minsAgo: number; reason?: string }): AuditEvent {
  return {
    id: `${agent_id}-${i}`,
    agent_id,
    event_type: p.event_type ?? (p.decision === "denied" ? "action_denied" : "action_allowed"),
    decision: p.decision,
    reason_code: p.reason ?? null,
    tx_signature: p.decision === "denied" ? null : TX(i),
    block_time: iso(p.minsAgo * MIN),
    created_at: iso(p.minsAgo * MIN),
    action_descriptor: { type: p.type, amount: p.amount, asset: p.asset, target: "deepbook · SUI/DEEP" },
  } as unknown as AuditEvent;
}

// Per-action trails incl. admin actions (item 5: record everything).
const TRAILS: Record<string, AuditEvent[]> = {
  "0xa1b2c3d4": [
    ev(1, "0xa1b2c3d4", { type: "deepbook_swap", decision: "allowed", amount: 0.4, asset: "SUI", minsAgo: 6 }),
    ev(2, "0xa1b2c3d4", { type: "deepbook_swap", decision: "denied", amount: 0.7, asset: "SUI", minsAgo: 12, reason: "over_per_tx_cap" }),
    ev(3, "0xa1b2c3d4", { type: "edit_policy", decision: "n/a", event_type: "policy_updated", minsAgo: 30 }),
    ev(4, "0xa1b2c3d4", { type: "deepbook_limit_order", decision: "allowed", amount: 25, asset: "USDC", minsAgo: 41 }),
    ev(5, "0xa1b2c3d4", { type: "deepbook_swap", decision: "allowed", amount: 0.5, asset: "SUI", minsAgo: 95 }),
  ],
  "0xb2c3d4e5": [
    ev(1, "0xb2c3d4e5", { type: "pause", decision: "n/a", event_type: "agent_paused", minsAgo: 180 }),
    ev(2, "0xb2c3d4e5", { type: "deepbook_swap", decision: "allowed", amount: 1, asset: "SUI", minsAgo: 200 }),
  ],
  "0xc3d4e5f6": [
    ev(1, "0xc3d4e5f6", { type: "revoke", decision: "n/a", event_type: "agent_revoked", minsAgo: 120 }),
    ev(2, "0xc3d4e5f6", { type: "deepbook_swap", decision: "denied", amount: 100, asset: "USDC", minsAgo: 110, reason: "policy_revoked" }),
    ev(3, "0xc3d4e5f6", { type: "deepbook_swap", decision: "denied", amount: 800, asset: "USDC", minsAgo: 480, reason: "over_per_tx_cap" }),
    ev(4, "0xc3d4e5f6", { type: "deepbook_swap", decision: "allowed", amount: 500, asset: "USDC", minsAgo: 600 }),
  ],
};

export function previewAudit(agentId: string, filter: "all" | "allowed" | "denied" = "all"): AuditEvent[] {
  const all = TRAILS[agentId] ?? [];
  return filter === "all" ? all : all.filter((e) => e.decision === filter);
}

export function previewFleetAudit(filter: "all" | "allowed" | "denied" = "all"): (AuditEvent & { agent_id: string | null })[] {
  const all = PREVIEW_AGENTS.flatMap((a) => previewAudit(a.id)).sort((x, y) => (x.block_time! < y.block_time! ? 1 : -1));
  return (filter === "all" ? all : all.filter((e) => e.decision === filter)) as (AuditEvent & { agent_id: string | null })[];
}
